import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import { z } from 'zod'

import { PersonColumnSettings } from '@/components/business/person-column'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { StatusTabOption } from '@/components/ui/status-tabs'
import { TableColumn } from '@/components/ui/table'
import { EMPTY_VALUE } from '@/constants'
import { AnnouncementRecipientModel, AnnouncementRecipientStatusEnum } from '@/models/features'
import {
  getAnnouncementResidentColumn,
  getRecipientStatusColor,
  getRecipientStatusLabel,
} from '@/pages/communication/announcement-details/announcement-details.page.utils'
import { CapitalizedBadge } from '@/pages/communication/announcement-details/announcement-details.styled'
import { formatDateOnly, formatTimeOnly } from '@/utils/date'

// Note: Backend status enum - single source of truth for actual DB statuses
export const notificationRecipientStatusEnum = AnnouncementRecipientStatusEnum.exclude([
  'clicked',
  'opened',
])
export type NotificationRecipientStatus = z.infer<typeof notificationRecipientStatusEnum>

// Note: Extend possible statuses to include 'All' which will be only indicated in UI
export const notificationStatusFilterEnum = z.enum([
  ...notificationRecipientStatusEnum.options,
  'All',
])
export type NotificationStatusFilter = z.infer<typeof notificationStatusFilterEnum>

export const notificationRecipientsTableFiltersSchema = z.object({
  limit: z.coerce.number().optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  status: notificationStatusFilterEnum.optional(),
})

export type NotificationRecipientsTableFilters = z.infer<
  typeof notificationRecipientsTableFiltersSchema
>

export const notificationRecipientsTableInitialFilters: NotificationRecipientsTableFilters = {
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: undefined,
  sortOrder: undefined,
  status: notificationStatusFilterEnum.enum.All,
}

export const getNotificationStatusOptions = (
  t: TFunction<['communication']>,
): StatusTabOption<NotificationStatusFilter>[] => [
  {
    label: t('communication:announcementDetails.recipientsTable.status.all'),
    value: notificationStatusFilterEnum.enum.All,
  },
  ...notificationRecipientStatusEnum.options.map((status) => ({
    color: getRecipientStatusColor(status),
    label: getRecipientStatusLabel(status, t),
    value: status,
  })),
]

export const getNotificationTableColumns = (
  t: TFunction<['communication']>,
  timezone: string,
  residentColumnSettings: PersonColumnSettings,
): TableColumn<AnnouncementRecipientModel>[] => [
  getAnnouncementResidentColumn(t, residentColumnSettings),
  {
    id: 'status',
    render: (recipient) => {
      if (!recipient.status) return EMPTY_VALUE
      return (
        <CapitalizedBadge
          color={getRecipientStatusColor(recipient.status) || 'neutral'}
          size="small"
          variant="light"
        >
          {recipient.status}
        </CapitalizedBadge>
      )
    },
    title: t('communication:announcementDetails.recipientsTable.columns.status'),
    width: '15rem',
  },
  {
    id: 'lastUpdated',
    render: (recipient) => {
      if (!recipient.updatedAt) return EMPTY_VALUE
      const zonedDate = toZonedTime(recipient.updatedAt, timezone)

      return `${formatDateOnly(zonedDate)}\n${formatTimeOnly(zonedDate)}`
    },
    title: t('communication:announcementDetails.recipientsTable.columns.lastUpdated'),
    width: '15rem',
  },
]
